import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, InjectionToken, Optional, Renderer2, RendererFactory2 } from '@angular/core';
import { Observable, of } from 'rxjs';
import { IIconDefinition, IIconParams } from './icons.interfaces';

export const DP_ICONS = new InjectionToken('dp_icons');

@Injectable()
export class IconsService {
  private _renderer: Renderer2;
  private readonly _svgDefinitions = new Map<string, IIconDefinition>();
  private _stringSvg!: string;

  constructor(
    private _rendererFactory: RendererFactory2,
    @Optional() @Inject(DOCUMENT) private _document: Document,
    @Optional() @Inject(DP_ICONS) icons?: IIconDefinition[],
  ) {
    this._renderer = this._rendererFactory.createRenderer(null, null);
    this.addIcon(...(icons || []));
  }

  public iconExists(iconName: string): Observable<boolean> {
    const icon = this._svgDefinitions.get(iconName);
    if (icon) {
      this._stringSvg = icon.icon;
      return of(true);
    }
    return of(false);
  }

  public addIcon(...icons: IIconDefinition[]): void {
    icons.forEach((icon) => {
      this._svgDefinitions.set(icon.name, icon);
    });
  }

  public getRenderedContent(iconParams: IIconParams): SVGElement {
    return this._colorizeSvg(
      this._setSvgAttribute(this._createSvgFromString(this._stringSvg), iconParams.size),
      iconParams.color,
      iconParams.stroke,
    );
  }

  private _createSvgFromString(str: string): SVGElement {
    const div = this._document.createElement('div');
    div.innerHTML = str;
    const svg: SVGElement | null = div.querySelector('svg');
    if (!svg) {
      throw new Error('SVG tag not found!');
    }
    return svg;
  }

  private _setSvgAttribute(svg: SVGElement, size: string): SVGElement {
    this._renderer.setAttribute(svg, 'width', size);
    this._renderer.setAttribute(svg, 'height', '100%');
    return svg;
  }

  private _colorizeSvg(svg: SVGElement, color: string, stroke?: string): SVGElement {
    this._renderer.setAttribute(svg, 'fill', color);
    this._renderer.setAttribute(svg, 'stroke', stroke || '#fff');
    return svg;
  }

  public _removeWhiteSpacesFromSvg(svg: SVGGraphicsElement): void {
    let maxDimensions = svg.getBBox();
    svg.setAttribute(
      'viewBox',
      [maxDimensions.x, maxDimensions.y, maxDimensions.width, maxDimensions.height]
        .map((dimension) => this._roundSvgDimensions(dimension))
        .join(' '),
    );
  }

  public _roundSvgDimensions(dimension: number): number {
    return Math.round((dimension + Number.EPSILON) * 100) / 100;
  }
}
